import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { SpinnerGap, CaretLeft, CaretRight, Phone, Envelope, CaretUp, CaretDown, CaretUpDown, MagnifyingGlass, X, PaperPlaneTilt, Gear } from '@phosphor-icons/react';
import { PersonLink } from '@/components/PersonLink';
import WhatsAppLink from '@/components/WhatsAppLink';
import ScopeToggle from '@/components/ScopeToggle';
import BulkEmailDialog from '@/components/BulkEmailDialog';
import PageSizeSelect, { loadPageSize, savePageSize } from '@/components/PageSizeSelect';
import useScopeFilter from '@/hooks/useScopeFilter';

const TIPO_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'CS', label: 'Consultor (CS)' },
  { value: 'BC', label: 'B2C' },
  { value: 'BB', label: 'B2B' },
];

/**
 * Cabeçalho de coluna ordenável. Toggle ASC↔DESC ao clicar; clique numa
 * coluna diferente reseta para DESC.
 */
function SortHeader({ label, sortKey, currentSort, currentDir, onSort, align = 'left', className = '' }) {
  const isActive = currentSort === sortKey;
  const Icon = isActive ? (currentDir === 'asc' ? CaretUp : CaretDown) : CaretUpDown;
  const justify = align === 'right' ? 'justify-end' : 'justify-start';
  return (
    <th className={`text-${align} text-[10px] uppercase tracking-[0.2em] font-medium px-4 py-3 ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`leads-sort-${sortKey}`}
        className={`inline-flex items-center gap-1 ${justify} ${isActive ? 'text-[#0F1829] font-semibold' : 'text-[#6B7280]'} hover:text-[#0F1829] transition-colors`}
      >
        {label}
        <Icon size={11} weight={isActive ? 'bold' : 'regular'} className={isActive ? '' : 'opacity-50'} />
      </button>
    </th>
  );
}

function Pagination({ page, totalPages, total, onPageChange, pageSize, onPageSizeChange }) {
  if (total <= 0) return null;
  return (
    <div data-testid="pagination" className="flex flex-col items-center gap-2 px-4 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
      <div className="flex items-center gap-4 flex-wrap justify-center">
        <p className="text-xs text-[#6B7280]">Página {page} de {totalPages} ({total} registos)</p>
        <PageSizeSelect value={pageSize} onChange={onPageSizeChange} testId="leads-page-size" />
      </div>
      {totalPages > 1 && (
      <div className="flex items-center gap-1">
        <button disabled={page <= 1} onClick={() => onPageChange(page - 1)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
          <CaretLeft size={12} /> Anterior
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let p;
          if (totalPages <= 5) p = i + 1;
          else if (page <= 3) p = i + 1;
          else if (page >= totalPages - 2) p = totalPages - 4 + i;
          else p = page - 2 + i;
          return (
            <button key={p} onClick={() => onPageChange(p)}
              className={`w-8 h-8 text-xs font-medium rounded border transition-colors ${p === page ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'border-[#E5E7EB] text-[#374151] hover:bg-white'}`}>
              {p}
            </button>
          );
        })}
        <button disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
          Seguinte <CaretRight size={12} />
        </button>
      </div>
      )}
    </div>
  );
}

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [scope, setScope] = useScopeFilter('all');
  const [pageSize, setPageSize] = useState(loadPageSize);
  const [sortBy, setSortBy] = useState('data');
  const [sortDir, setSortDir] = useState('desc');
  // Filtros
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState(''); // valor debounced
  const [tipoFilter, setTipoFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [estados, setEstados] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Selecção múltipla para envio bulk de emails
  const [selected, setSelected] = useState({});  // { [ID_Negocio]: true }
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [bulkToast, setBulkToast] = useState(null);
  const navigate = useNavigate();
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  // Debounce do search input (350ms)
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Lista de estados disponíveis para o tipo seleccionado
  useEffect(() => {
    api.get('/leads/estados')
      .then(r => setEstados(r?.data?.items || []))
      .catch(() => setEstados([]));
  }, []);

  const estadosForTipo = useMemo(() => {
    if (!tipoFilter) return estados;
    return estados.filter(e => e.TipoLead === tipoFilter);
  }, [tipoFilter, estados]);

  const fetchLeads = useCallback((p) => {
    setLoading(true);
    api.post('/leads/list', {
      page: p, page_size: pageSize, scope,
      sort_by: sortBy, sort_dir: sortDir,
      q, tipo: tipoFilter, estado: estadoFilter,
      date_from: dateFrom, date_to: dateTo,
    })
      .then(r => {
        setLeads(r.data?.data || []);
        setTotalPages(r.data?.total_pages || 1);
        setTotal(r.data?.total || 0);
        setPage(r.data?.page || p);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [scope, sortBy, sortDir, q, tipoFilter, estadoFilter, dateFrom, dateTo, pageSize]);

  const handlePageSizeChange = (v) => {
    savePageSize(v);
    setPageSize(v);
  };

  useEffect(() => { fetchLeads(1); }, [fetchLeads]);

  // Ao mudar de página / filtros, limpa selecção
  useEffect(() => { setSelected({}); }, [page, q, tipoFilter, estadoFilter, dateFrom, dateTo, scope]);

  const selectedIds = useMemo(() => Object.keys(selected).filter(k => selected[k]).map(k => parseInt(k)), [selected]);
  const selectedLeads = useMemo(() => leads.filter(l => selected[l.ID_Negocio]), [leads, selected]);
  const allSelected = leads.length > 0 && leads.every(l => selected[l.ID_Negocio]);
  const someSelected = leads.some(l => selected[l.ID_Negocio]);
  const toggleAll = () => {
    if (allSelected) { setSelected({}); return; }
    const next = {};
    leads.forEach(l => { next[l.ID_Negocio] = true; });
    setSelected(next);
  };
  const toggleOne = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  const hasFilters = q || tipoFilter || estadoFilter || dateFrom || dateTo;
  const clearFilters = () => {
    setSearchInput(''); setQ('');
    setTipoFilter(''); setEstadoFilter('');
    setDateFrom(''); setDateTo('');
  };

  return (
    <div data-testid="leads-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Leads</h1>
          <p className="text-sm text-[#6B7280] mt-1">{total} leads registadas</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ScopeToggle value={scope} onChange={setScope} testid="leads-scope-toggle" />
          <Link to="/leads/tratar" data-testid="treat-leads-btn" className="px-4 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#002480] whitespace-nowrap">Tratar Leads</Link>
          <Link to="/leads/pesquisa" data-testid="search-leads-btn" className="px-4 py-2 border border-[#E5E7EB] text-sm font-medium rounded-md hover:bg-[#F3F4F6] whitespace-nowrap">Pesquisar</Link>
          <Link to="/leads/analise" data-testid="analysis-leads-btn" className="px-4 py-2 border border-[#E5E7EB] text-sm font-medium rounded-md hover:bg-[#F3F4F6] whitespace-nowrap">Análise</Link>
          <Link to="/leads/nova" data-testid="new-lead-btn" className="px-4 py-2 border border-[#E5E7EB] text-sm font-medium rounded-md hover:bg-[#F3F4F6] whitespace-nowrap">Nova Lead</Link>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="leads-filters">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Pesquisar por nome, email ou cliente…"
            data-testid="leads-filter-search"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E7EB] rounded-md focus:ring-2 focus:ring-[#0F1829]/20 focus:border-[#0F1829] outline-none"
          />
        </div>
        <select
          value={tipoFilter}
          onChange={e => { setTipoFilter(e.target.value); setEstadoFilter(''); }}
          data-testid="leads-filter-tipo"
          className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-md min-w-[140px]"
        >
          {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={estadoFilter}
          onChange={e => setEstadoFilter(e.target.value)}
          data-testid="leads-filter-estado"
          className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-md min-w-[180px]"
        >
          <option value="">Todos os estados</option>
          {estadosForTipo.map(e => (
            <option key={`${e.TipoLead}-${e.ID_EstadoNegocio}`} value={e.ID_EstadoNegocio}>
              {e.TipoLead} · {e.Descricao}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          data-testid="leads-filter-date-from"
          title="Data desde"
          className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-md"
        />
        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          data-testid="leads-filter-date-to"
          title="Data até"
          className="px-3 py-2 text-sm border border-[#E5E7EB] rounded-md"
        />
        {hasFilters && (
          <button
            onClick={clearFilters}
            data-testid="leads-filters-clear"
            className="flex items-center gap-1 px-3 py-2 text-sm text-[#6B7280] hover:text-[#0F1829]"
          >
            <X size={14} /> Limpar
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        {/* Toolbar de selecção — visível quando há leads escolhidas */}
        {selectedIds.length > 0 && (
          <div data-testid="bulk-toolbar" className="bg-[#0F1829] text-white px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 text-sm">
              <span className="font-semibold" data-testid="bulk-selected-count">{selectedIds.length} lead(s) seleccionada(s)</span>
              <button onClick={() => setSelected({})} data-testid="bulk-clear"
                className="text-xs text-white/70 hover:text-white underline">Limpar</button>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/perfil/email-config" data-testid="bulk-email-config-link"
                className="text-xs text-white/70 hover:text-white underline flex items-center gap-1">
                <Gear size={12} /> Config SMTP
              </Link>
              <button onClick={() => setShowBulkEmail(true)} data-testid="bulk-email-open"
                className="px-3 py-1.5 text-xs font-medium bg-white text-[#0F1829] rounded-md hover:bg-[#F3F4F6] flex items-center gap-1.5">
                <PaperPlaneTilt size={13} /> Enviar Email
              </button>
            </div>
          </div>
        )}

        {bulkToast && (
          <div data-testid="bulk-toast" className={`px-4 py-2.5 text-sm font-medium border-b ${
            bulkToast.type === 'success' ? 'bg-green-50 text-green-800 border-green-200' : 'bg-red-50 text-red-800 border-red-200'
          }`}>
            {bulkToast.msg}
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-12"><SpinnerGap size={24} className="animate-spin text-[#0F1829]" /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <th className="px-3 py-3 w-10">
                  <input type="checkbox" checked={allSelected}
                    ref={el => { if (el) el.indeterminate = !allSelected && someSelected; }}
                    onChange={toggleAll} data-testid="bulk-select-all"
                    className="w-4 h-4 accent-[#0F1829] cursor-pointer" title="Seleccionar tudo" />
                </th>
                <SortHeader label="ID" sortKey="id" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Tipo" sortKey="tipo" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Contacto" sortKey="contacto" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Telefone" sortKey="telefone" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Cliente" sortKey="cliente" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Estado" sortKey="estado" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                <SortHeader label="Data" sortKey="data" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                <th className="text-left text-[10px] uppercase tracking-[0.2em] font-medium px-4 py-3 text-[#6B7280]">Últ. Estado</th>
                <SortHeader label="Valor" sortKey="valor" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} align="right" />
              </tr></thead>
              <tbody>
                {leads.map(l => (
                  <tr key={l.ID_Negocio} onClick={() => navigate(`/negocios/${l.ID_Negocio}`)} className={`border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer transition-colors ${selected[l.ID_Negocio] ? 'bg-[#0F1829]/5' : ''}`}>
                    <td className="px-3 py-3 w-10" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={!!selected[l.ID_Negocio]}
                        onChange={() => toggleOne(l.ID_Negocio)}
                        data-testid={`bulk-select-${l.ID_Negocio}`}
                        className="w-4 h-4 accent-[#0F1829] cursor-pointer" />
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-[#0F1829] underline">{l.ID_Negocio}</td>
                    <td className="px-4 py-3">
                      <span data-testid={`lead-tipo-${l.ID_Negocio}`} className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-[#F3F4F6] text-[#0F1829] border-[#E5E7EB]">
                        {l.TipoNegocio || l.ID_TipoNegocio || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-[#111827]">
                      {l.NomeContacto || '-'}
                      {l.EmailContacto && (
                        <a href={`mailto:${l.EmailContacto}`} onClick={e => e.stopPropagation()} title={l.EmailContacto}
                          data-testid={`lead-email-${l.ID_Negocio}`}
                          className="block text-[11px] text-[#6B7280] hover:text-[#0F1829] hover:underline mt-0.5 font-normal flex items-center gap-1">
                          <Envelope size={11} /> {l.EmailContacto}
                        </a>
                      )}
                      {l.CampanhaDesc && (
                        <span
                          data-testid={`lead-campanha-${l.ID_Negocio}`}
                          title={l.CampanhaId ? `Landing Page: ${l.CampanhaId}` : ''}
                          className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-semibold px-1.5 py-0.5 rounded bg-[#F5F2EB] text-[#645E50] border border-[#E5E1D3]"
                        >
                          {l.CampanhaDesc}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#111827] whitespace-nowrap">
                      {l.TelemovelContacto ? (
                        <span className="inline-flex items-center gap-2">
                          <a href={`tel:${l.TelemovelContacto}`} onClick={e => e.stopPropagation()}
                            data-testid={`lead-phone-${l.ID_Negocio}`}
                            className="inline-flex items-center gap-1 font-mono text-[#0F1829] hover:underline">
                            <Phone size={12} weight="bold" /> {l.TelemovelContacto}
                          </a>
                          <WhatsAppLink
                            phone={l.TelemovelContacto}
                            idNegocio={l.ID_Negocio}
                            firstName={(l.NomeContacto || '').split(' ')[0] || ''}
                            size={12}
                          />
                        </span>
                      ) : <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <PersonLink id={l.ID_Cliente} name={l.ClienteNome} />
                      {l.IsOwn === 0 && l.ConsultorNome && (
                        <span data-testid={`lead-via-${l.ID_Negocio}`}
                          title={`Consultor: ${l.ConsultorNome.trim()}`}
                          className="ml-2 inline-flex items-center text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200">
                          via {l.ConsultorNome.trim().split(' ')[0]}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-blue-50 text-[#0F1829] border-[#0F1829]/20">
                        {l.EstadoAtual || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]" data-testid={`lead-data-${l.ID_Negocio}`}>{l.DataRegisto || '-'}</td>
                    <td className="px-4 py-3 text-sm text-[#6B7280]" data-testid={`lead-ultimo-estado-${l.ID_Negocio}`}>{l.DataUltimoEstado || '-'}</td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-[#111827]">{fmt(l.ValorPrevisto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {leads.length === 0 && <p className="text-center py-6 text-sm text-[#6B7280]">Sem leads registadas</p>}
            <Pagination page={page} totalPages={totalPages} total={total} onPageChange={fetchLeads}
              pageSize={pageSize} onPageSizeChange={handlePageSizeChange} />
          </>
        )}
      </div>

      {showBulkEmail && (
        <BulkEmailDialog
          selectedLeads={selectedLeads}
          onClose={() => setShowBulkEmail(false)}
          onSent={(r) => {
            const msg = `${r.sent_count} enviado(s), ${r.failed_count} falha(s), ${r.skipped_count} ignorada(s)`;
            setBulkToast({ type: r.failed_count > 0 ? 'error' : 'success', msg });
            setTimeout(() => setBulkToast(null), 8000);
            if (r.sent_count > 0) setSelected({});
          }}
        />
      )}
    </div>
  );
}
